import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { LuFileSpreadsheet } from 'react-icons/lu'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { SubmitHandler } from 'react-hook-form'
import type { TCategoryId, TClusterForm } from '~/types/cluster'

import FeaturesFieldArray from './FeaturesFieldArray'
import SubCategoriesContainer from './SubCategoriesContainer'

import Button from '~/components/Buttons/Button'
import MwMLoader from '~/components/MwMLoader'
import { FORM_ERRORS } from '~/const'
import { FEATURE_SELECT_VALUES } from '~/const/clusters'
import { useRequiredParams } from '~/hooks/useRequiredParams'
import { keys as clustersKeys, useClusterFeaturesQuery, useSaveClusterFeaturesMutation } from '~/queries/clusters'
import { displayToast } from '~/utils/toast'

import './styles.scss'

type TClusterFormTemplate = Omit<TClusterForm, 'value' | 'select_options' | 'sub_category'> & {
  sub_category: {
    label: string
    value: string
  } | null
}

type FormValues = {
  features: TClusterFormTemplate[]
}

const validationSchema = Yup.object().shape({
  features: Yup.array()
    .of(
      Yup.object({
        category_id: Yup.mixed<TCategoryId>().required(),
        category_name: Yup.string().required(),
        cluster_ids: Yup.array().of(Yup.string().required()).required().nullable(),
        enabled: Yup.boolean().required(),
        id: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
        input_type: Yup.string()
          .oneOf(['BOOL', 'MULTI SELECT', 'NUMBER', 'SELECT', 'TEXT', 'TEXTAREA', 'FILE', 'MULTI TEXT'])
          .required(FORM_ERRORS.REQUIRED),
        label: Yup.string().trim().required(FORM_ERRORS.REQUIRED),
        position: Yup.number().required(FORM_ERRORS.REQUIRED),
        sub_category: Yup.object({
          label: Yup.string().required(),
          value: Yup.string().required(),
        }).nullable(),
      })
    )
    .required(),
})

const Feature = (): ReactElement => {
  /** Local state */

  const queryClient = useQueryClient()

  const { clusterId } = useRequiredParams<{ clusterId: string }>()

  const [subCategories, setSubCategories] = useState<string[]>([])

  /** Queries & Mutations */

  const { data: featuresData, isLoading } = useClusterFeaturesQuery(clusterId)

  const { mutateAsync: saveFeatures } = useSaveClusterFeaturesMutation()

  /** Query dependent state */

  const methods = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    values: {
      features: featuresData
        ? featuresData.map((f) => ({
            ...f,
            sub_category: f.sub_category ? { label: f.sub_category, value: f.sub_category } : null,
          }))
        : [],
    },
  })

  const {
    formState: { isDirty, isSubmitting },
    handleSubmit,
  } = methods

  /** Handlers */

  const handleAddSubCategory = () => setSubCategories((prev) => [...prev, ''])

  const handleEditSubCategory = (oldSubCategory: string, newSubCategory: string, index: number) =>
    setSubCategories((prevState) =>
      prevState.map((subCategory, currentIndex) =>
        subCategory === oldSubCategory && index === currentIndex ? newSubCategory : subCategory
      )
    )

  const handleFormSubmit: SubmitHandler<FormValues> = async (values) => {
    const updatedValuesPositions = values.features.map((feature, index) => ({
      ...feature,
      position: index,
      select_options: FEATURE_SELECT_VALUES,
      sub_category: feature.sub_category?.value,
    }))

    try {
      await saveFeatures({ clusterId, data: updatedValuesPositions })
      await queryClient.invalidateQueries({ queryKey: clustersKeys.features(clusterId) })
      displayToast('Features successfully updated', 'success')
    } catch (e) {
      captureException(e)
      displayToast('Features could not be updated', 'error')
    }
  }

  /** Effects */

  useEffect(() => {
    if (featuresData) {
      setSubCategories([
        ...new Set(featuresData.filter((item) => item.sub_category).map((item) => item.sub_category as string)),
      ])
    }
  }, [featuresData])

  /** Render */

  return (
    <div className="features-manager-view">
      {isLoading ? (
        <MwMLoader category="general" />
      ) : (
        <FormProvider {...methods}>
          <form className="features-form" onSubmit={handleSubmit(handleFormSubmit)}>
            <div className="subcategories-container">
              <div className="header">
                <LuFileSpreadsheet color="#ff5301" size={17} />
                <span>Sub-Categories</span>
              </div>
              <SubCategoriesContainer
                onAddSubCategoryClick={handleAddSubCategory}
                onEditSubCategory={handleEditSubCategory}
                subCategories={subCategories}
              />
            </div>
            <div className="features-container">
              <FeaturesFieldArray clusterId={clusterId} subCategories={subCategories} />
            </div>
            <div className="form-footer">
              <Button disabled={!isDirty || isSubmitting} type="submit">
                Save
              </Button>
            </div>
          </form>
        </FormProvider>
      )}
    </div>
  )
}

export default Feature
